import * as autobind from 'autobind';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { Grid, Icon, Message } from 'semantic-ui-react';
import { NMClustersStore, NMClusterDto } from 'stores/configuration/events-n-requests/non-machine-related/clusters-store';
import { ItemState, Query, OrderDefinition } from 'stores/dataStore';
import { CommandResult } from 'stores/types';
import { nameof } from 'utils/object';
import { TableModel, TableView } from 'widgets/collections/table';
import { TextBoxFilter } from 'widgets/collections/table-filters/textbox-filter';
import NewNMClusterView from './new-nm-cluster';
import ChangeNMClusterView from './edit-nm-cluster';
import { OptionsFilter } from 'widgets/collections/table-filters/option-filter';
import { Categories } from 'stores/requests/requests-store';

export interface NMClusterListProps extends WithTranslation, RouteComponentProps {
  nmClusters: NMClustersStore;
}

export interface NMClusterListState {
  query: Query;
  newNMClusterShown: boolean;
  changeNMClusterShown: boolean;
  activeFilters: string[];
  selectedItem: any;
  someFilterOpened: boolean;
}

@connect(['nmClusters', NMClustersStore])
class NMClusterListPage extends React.Component<NMClusterListProps, NMClusterListState> {
  handleOnEnterKeydown = item => {
    this.setState({ selectedItem: item }, () => this.onEditItem());
  };

  handleOnRowDoubleClick: (item: unknown) => void;

  constructor(props) {
    super(props);
    this.state = {
      query: { searchQuery: '', orderBy: [{ direction: 'Descending', field: 'modifiedOn', useProfile: false }], skip: 0, take: 10 },
      newNMClusterShown: false,
      changeNMClusterShown: false,
      activeFilters: [],
      selectedItem: null,
      someFilterOpened: false
    };
  }

  componentDidMount() {
    this.load();
  }

  @autobind
  private load() {
    this.props.nmClusters.getAllAsync(this.state.query);
  }

  @autobind
  private handleOrderBy(orderBy: OrderDefinition[]) {
    this.setState(
      {
        query: Object.assign(this.state.query, {
          orderBy: [...orderBy, { direction: 'Descending', field: 'modifiedOn', useProfile: false }]
        })
      },
      this.load
    );
  }

  @autobind
  private handlePageChange(skip: number, take: number) {
    this.setState({ query: Object.assign(this.state.query, { skip, take }) });
    this.load();
  }

  @autobind
  private async onSaveRow(item: NMClusterDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.nmClusters.saveAsync(item, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private async onDelete(item: NMClusterDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.nmClusters.deleteAsync(item.id, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private handleFilterChange(filters: { id: string; filter: any }[]) {
    const filter = filters.map(f => f.filter);
    const activeFilters = filters.map(f => f.id);

    const query = Object.assign(this.state.query, { filter, skip: 0 });
    this.setState({ query, activeFilters }, () => this.load());
  }

  @autobind
  private onNewItem() {
    this.setState({ newNMClusterShown: true });
  }

  @autobind
  private onNewItemClosed(isSuccess: boolean) {
    this.setState({ newNMClusterShown: false });
    if (isSuccess) this.load();
  }

  @autobind
  private onEditItem() {
    this.setState({ changeNMClusterShown: true });
  }

  @autobind
  private onEditItemClosed(isSuccess: boolean) {
    this.setState({ changeNMClusterShown: false });
    if (isSuccess) this.load();
  }

  handleOnActivateFilter = (visible: boolean) => {
    this.setState({ someFilterOpened: visible });
  };

  public render() {
    const { t } = this.props as any;
    const { activeFilters, someFilterOpened } = this.state;

    const tableModel = {
      columns: [
        {
          title: t('Name'),
          tooltipRenderer: true,
          renderer: data => <span>{data.name}</span>,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by name')}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<NMClusterDto>('name'), `contains(${nameof<NMClusterDto>('name')}, '${value}')`)}
              onClear={() => onClear(nameof<NMClusterDto>('name'))}
              active={activeFilters.includes(nameof<NMClusterDto>('name'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: nameof<NMClusterDto>('name'),
            useProfile: false
          }
        },
        {
          title: t('Category'),
          tooltipRenderer: true,
          newClassFromComponent: data => 'table__more-text-long',
          renderer: data => (Categories[data.category] ? data.category : ''),
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <OptionsFilter
              filterTitle={t('Filter by category')}
              options={[
                { text: t('Technical'), value: 'Technical' },
                { text: t('Functional'), value: 'Functional' }
              ]}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<NMClusterDto>('category'), { [nameof<NMClusterDto>('category')]: value })}
              onClear={() => onClear(nameof<NMClusterDto>('category'))}
              active={activeFilters.includes(nameof<NMClusterDto>('category'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: 'category',
            useProfile: false
          }
        }
      ],
      data: this.props.nmClusters.state
    } as TableModel<NMClusterDto>;
    return (
      <>
        <Grid className="event-types-list-grid">
          {this.props.nmClusters.state.result && !this.props.nmClusters.state.result.isSuccess && (
            <Grid.Row className="event-types-list-error-row">
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.props.nmClusters.state.result.messages.map(o => o.body)}
              />
            </Grid.Row>
          )}
          <Grid.Row className="event-types-list-items-row request-list__table-view">
            <TableView
              /////////////////For build table keyboard navegation/////////////////
              selectable={!this.state.newNMClusterShown && !this.state.changeNMClusterShown}
              maxSelection={1}
              onHideCheckbox={true}
              selectionType={'allRow'}
              onEnterKeydown={this.handleOnEnterKeydown}
              onRowDoubleClick={this.handleOnRowDoubleClick}
              preventEnterKeyDownEvent={someFilterOpened}
              //showActionsConfirmModal={true}
              /////////////////For build table keyboard navegation/////////////////
              model={tableModel}
              extraActions={[
                {
                  content: (
                    <>
                      <Icon name="edit" />
                      &nbsp;{t('Edit')}
                    </>
                  ),
                  onClick: item => {
                    this.setState({ selectedItem: item }, () => this.onEditItem());
                  }
                }
              ]}
              onOrderByChanged={this.handleOrderBy}
              onNewItem={this.onNewItem}
              onRefresh={this.load}
              canEdit={false}
              canDelete={true}
              onDeleteRow={this.onDelete}
              onSaveRow={this.onSaveRow}
              onPageChange={this.handlePageChange}
              onFilterChange={this.handleFilterChange}
              canCreateNew={true}
              createNewButtonTitle={t('Add Cluster')}
            ></TableView>
          </Grid.Row>
        </Grid>
        {(this.state.newNMClusterShown && <NewNMClusterView onClose={this.onNewItemClosed} {...this.props} />) ||
          (this.state.changeNMClusterShown && <ChangeNMClusterView onClose={this.onEditItemClosed} currentDto={this.state.selectedItem} />)}
      </>
    );
  }
}

export default withTranslation()(NMClusterListPage);
